import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import Navigation from '../components/Navigation';
import ElegantPageBg from '../components/ElegantPageBg';
import { locationService, ticketService, promocodeService } from '../services/api';
import { ensureMediaOriginLoaded } from '../services/siteConfig';
import { toYmd, isEventTicketType } from '../utils/dateRange';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { FiMinus, FiPlus } from 'react-icons/fi';
import './TicketSelection.css';

// Default ticket thumbnail (from old mock data, used when API does not provide an image)
const DEFAULT_TICKET_IMAGE = 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=300&h=200&fit=crop';

const TicketSelection = () => {
  const { bookingData, updateTickets, setAppliedPromo, clearAppliedPromo } = useBooking();
  const [ticketPrices, setTicketPrices] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState({});
  const [couponInput, setCouponInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoApplying, setPromoApplying] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const appliedPromo = bookingData.appliedPromo || null;

  useEffect(() => {
    if (bookingData.location) {
      fetchTickets();
    } else {
      navigate('/');
    }
  }, [bookingData.location]);

  const fetchTickets = async () => {
    if (!bookingData.location?.id) {
      setLoading(false);
      return;
    }
    try {
      await ensureMediaOriginLoaded();
      const res = await locationService.getTickets(bookingData.location.id);
      const data = res.data;
      let list = Array.isArray(data) ? data : (data?.results ?? []);

      // If no location-specific prices are set (e.g. "Set Ticket Price" not done for this location),
      // fall back to all ticket types with their base_price so tickets still show
      if (list.length === 0) {
        try {
          const typesRes = await ticketService.getAll();
          const typesData = typesRes.data;
          const types = Array.isArray(typesData) ? typesData : (typesData?.results ?? []);
          list = types.map((tt) => ({
            ticket_type: tt,
            ticket_type_id: tt.id,
            price: tt.base_price,
          }));
        } catch (e) {
          console.warn('Could not load ticket types as fallback:', e);
        }
      }

      setTicketPrices(list);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setTicketPrices([]);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = (ticketTypeId, change) => {
    setSelectedTickets(prev => {
      const newValue = Math.max(0, (prev[ticketTypeId] || 0) + change);
      return { ...prev, [ticketTypeId]: newValue };
    });
  };

  const getTicketTypeId = (tp) => tp.ticket_type?.id ?? tp.ticket_type_id ?? tp.id;

  const visitDateStr = bookingData.visitDate;
  const visibleTicketPrices = useMemo(() => {
    if (!visitDateStr) return ticketPrices;
    return ticketPrices.filter((tp) => {
      const tt = tp.ticket_type;
      if (!isEventTicketType(tt)) return true;
      const r = tt.event_date_range;
      if (!r?.from || !r?.to) return false;
      const fr = toYmd(r.from);
      const to = toYmd(r.to);
      if (!fr || !to) return false;
      return visitDateStr >= fr && visitDateStr <= to;
    });
  }, [ticketPrices, visitDateStr]);

  useEffect(() => {
    const initial = {};
    visibleTicketPrices.forEach((tp) => {
      const tid = getTicketTypeId(tp);
      if (tid) initial[tid] = 0;
    });
    setSelectedTickets(initial);
  }, [visibleTicketPrices]);

  const calculateSubtotal = () => {
    let sum = 0;
    visibleTicketPrices.forEach((tp) => {
      const tid = getTicketTypeId(tp);
      const qty = selectedTickets[tid] || 0;
      const price = parseFloat(tp.price ?? tp.unit_price ?? 0);
      sum += price * qty;
    });
    return sum;
  };

  const applyPromoDiscount = (subtotal, promo) => {
    if (!promo || subtotal <= 0) return { discount: 0, total: subtotal };
    const v = parseFloat(promo.discount_value) || 0;
    let discount = 0;
    if (promo.discount_type === 'percent') {
      discount = Math.min(subtotal * (v / 100), subtotal);
    } else {
      discount = Math.min(v, subtotal);
    }
    return { discount, total: Math.max(0, subtotal - discount) };
  };

  const handleApplyPromo = async () => {
    const code = (couponInput || '').trim();
    if (!code) {
      setPromoError('Please enter a promo code.');
      return;
    }
    setPromoError('');
    setPromoApplying(true);
    try {
      const res = await promocodeService.validate(code);
      setAppliedPromo(res.data);
      setPromoError('');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid or expired promo code.';
      setPromoError(typeof msg === 'string' ? msg : 'Invalid promo code.');
      setAppliedPromo(null);
    } finally {
      setPromoApplying(false);
    }
  };

  const handleRemovePromo = () => {
    clearAppliedPromo();
    setCouponInput('');
    setPromoError('');
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const { total } = applyPromoDiscount(subtotal, appliedPromo);
    return total;
  };

  const handleContinue = () => {
    const tickets = visibleTicketPrices
      .filter((tp) => (selectedTickets[getTicketTypeId(tp)] || 0) > 0)
      .map((tp) => {
        const tid = getTicketTypeId(tp);
        const price = parseFloat(tp.price ?? tp.unit_price ?? 0);
        return {
          ticket_type_id: tid,
          quantity: selectedTickets[tid],
          unit_price: price,
        };
      });

    if (tickets.length === 0) {
      return;
    }

    updateTickets(tickets);
    navigate('/billing');
  };

  if (loading) {
    return (
      <div className="ticket-selection">
        <ElegantPageBg />
        <Navigation />
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading ticket options...</p>
          </div>
        </div>
      </div>
    );
  }

  const total = calculateTotal();
  const hasTickets = Object.values(selectedTickets).some(qty => qty > 0);
  const totalVisitors = Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);

  return (
    <div className="ticket-selection">
      <ElegantPageBg />
      <Navigation />
      <div className="container">
        <div className="tickets-main-grid">
          {/* Right Side - Tickets List Card */}
          <div className="tickets-selection-card">
            <div className="tickets-card-header">
              <div className="tickets-card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="tickets-card-title-wrapper">
                <h3 className="tickets-card-title">REGULAR TICKET</h3>
                <p className="tickets-card-subtitle">Unlimited access to all rides</p>
              </div>
              <button className="collapse-btn">−</button>
            </div>

            <div className="tickets-list-vertical">
              {visibleTicketPrices.length === 0 ? (
                <p className="ticket-selection-empty">
                  No tickets are available for your selected visit date. Please go back and choose another date.
                </p>
              ) : null}
              {visibleTicketPrices.map((tp) => {
                const ticketType = tp.ticket_type || { id: tp.ticket_type_id, name: 'Ticket', description: '', image: null };
                const tid = ticketType.id ?? tp.ticket_type_id;
                const quantity = selectedTickets[tid] || 0;
                const isSelected = quantity > 0;
                
                const price = parseFloat(tp.price ?? tp.unit_price ?? 0);
                const rawThumb =
                  isEventTicketType(ticketType) && ticketType.event_thumbnail
                    ? ticketType.event_thumbnail
                    : ticketType.image;
                const thumbSrc = rawThumb
                  ? resolveMediaUrl(rawThumb) || rawThumb
                  : DEFAULT_TICKET_IMAGE;
                return (
                  <div
                    key={tid}
                    className={`ticket-row ${isSelected ? 'selected' : ''}`}
                  >
                    <div className="ticket-row-left">
                      <div className="ticket-row-image">
                        <img
                          src={thumbSrc}
                          alt={ticketType.name}
                          onError={(e) => {
                            e.target.src = DEFAULT_TICKET_IMAGE;
                          }}
                        />
                      </div>
                      <div className="ticket-row-info">
                        <h4 className="ticket-row-name">
                          {ticketType.name}
                          {isEventTicketType(ticketType) ? (
                            <span className="ticket-event-pill">Event</span>
                          ) : null}
                        </h4>
                        {isEventTicketType(ticketType) && ticketType.event_date_range?.from && ticketType.event_date_range?.to ? (
                          <p className="ticket-event-dates">
                            {ticketType.event_date_range.from} — {ticketType.event_date_range.to}
                          </p>
                        ) : null}
                        <p className="ticket-row-price">₹{price.toFixed(2)}</p>
                        {ticketType.description && (
                          <p className="ticket-row-description">{ticketType.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="ticket-row-right">
                      {quantity === 0 ? (
                        <button
                          className="add-btn"
                          onClick={() => updateQuantity(tid, 1)}
                        >
                          ADD
                        </button>
                      ) : (
                        <div className="quantity-control">
                          <button
                            className="qty-btn minus"
                            onClick={() => updateQuantity(tid, -1)}
                          >
                            <FiMinus />
                          </button>
                          <span className="qty-value">{quantity}</span>
                          <button
                            className="qty-btn plus"
                            onClick={() => updateQuantity(tid, 1)}
                          >
                            <FiPlus />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Coupon Code Section */}
            <div className="coupon-section">
              <input
                type="text"
                className="coupon-input"
                placeholder="Enter Coupon Code"
                value={couponInput}
                onChange={(e) => {
                  setCouponInput(e.target.value);
                  if (promoError) setPromoError('');
                }}
                disabled={!!appliedPromo}
              />
              <button
                type="button"
                className="coupon-apply-btn"
                onClick={handleApplyPromo}
                disabled={promoApplying || !!appliedPromo}
              >
                {promoApplying ? 'Applying...' : 'Apply'}
              </button>
            </div>
            {appliedPromo && (
              <div className="promo-applied-msg">
                <span>{appliedPromo.code} applied</span>
                <button type="button" className="promo-remove-link" onClick={handleRemovePromo}>
                  Remove
                </button>
              </div>
            )}
            {promoError && <div className="promo-error-msg">{promoError}</div>}

            <div className="tickets-card-footer">
              <div className="footer-total">
                {appliedPromo && (() => {
                  const st = calculateSubtotal();
                  const { discount, total: tot } = applyPromoDiscount(st, appliedPromo);
                  return (
                    <>
                      <span className="footer-subtotal-label">Subtotal ₹{st.toFixed(2)}</span>
                      <span className="footer-discount-label">Discount ({appliedPromo.code}) -₹{discount.toFixed(2)}</span>
                      <span className="footer-total-label">₹{tot.toFixed(2)}</span>
                    </>
                  );
                })()}
                {!appliedPromo && <span className="footer-total-label">₹{total.toFixed(2)}</span>}
                <span className="footer-tax-label">(inclusive of all taxes)</span>
              </div>
              <button
                className={`confirm-proceed-btn ${hasTickets ? 'active' : 'disabled'}`}
                onClick={handleContinue}
                disabled={!hasTickets}
              >
                Confirm & Proceed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketSelection;
